/**
 * SIL-1559 - debounced, serialized autosave for the buyer-verification draft.
 *
 * The modal used to persist a step only on Continue, so anything typed and then
 * abandoned (X, overlay, Escape, Back) was lost. This hook owns the save
 * policy; the modal owns which fields belong to which step.
 *
 *  - `schedule(scope, patch)` debounces; a patch identical to the last one the
 *    server accepted for that scope is dropped without a request.
 *  - Saves are serialized on one promise chain and read the LATEST pending
 *    patch when they run, so two saves can never race and the newest edit wins.
 *  - A failed save never blocks typing. The patch stays pending and is retried
 *    with backoff (bounded), and any new edit retries immediately.
 *  - `flush()` cancels the debounce and drains - used by Back, close, Continue
 *    and `visibilitychange`/`pagehide`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { BuyerVerificationDraft, BuyerVerificationResponse } from '@siliconx/shared';

export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export const AUTOSAVE_DEBOUNCE_MS = 800;
const RETRY_BASE_MS = 3000;
const MAX_AUTO_RETRIES = 3;

interface Pending {
  scope: string;
  patch: BuyerVerificationDraft;
  key: string;
  /** A user-initiated save (Continue): never re-queued for background retry. */
  explicit: boolean;
}

export interface UseBuyerDraftAutosaveOptions {
  save: (patch: BuyerVerificationDraft) => Promise<BuyerVerificationResponse>;
  onSaved?: (response: BuyerVerificationResponse) => void;
  /** False while the form is unseeded, read-only, or not the admin's to edit. */
  enabled: boolean;
  debounceMs?: number;
}

export interface BuyerDraftAutosave {
  status: AutosaveStatus;
  /** Debounced save of `patch`; no-op when unchanged vs the last saved value. */
  schedule: (scope: string, patch: BuyerVerificationDraft, options?: { explicit?: boolean }) => void;
  /** Record `patch` as already persisted (the server-seeded baseline). */
  markSaved: (scope: string, patch: BuyerVerificationDraft) => void;
  /** Save anything pending now. Resolves false when the last save failed. */
  flush: () => Promise<boolean>;
  /** The error from the most recent failed save, for the Continue path. */
  lastError: () => unknown;
}

export function useBuyerDraftAutosave({
  save,
  onSaved,
  enabled,
  debounceMs = AUTOSAVE_DEBOUNCE_MS,
}: UseBuyerDraftAutosaveOptions): BuyerDraftAutosave {
  const [status, setStatus] = useState<AutosaveStatus>('idle');

  const saveRef = useRef(save);
  const onSavedRef = useRef(onSaved);
  const enabledRef = useRef(enabled);
  saveRef.current = save;
  onSavedRef.current = onSaved;
  enabledRef.current = enabled;

  const pending = useRef<Pending | null>(null);
  const lastSaved = useRef(new Map<string, string>());
  const chain = useRef<Promise<boolean>>(Promise.resolve(true));
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retries = useRef(0);
  const error = useRef<unknown>(null);
  const unmounted = useRef(false);
  // `doSave` schedules retries through `drain`, which wraps `doSave`; a ref
  // breaks the cycle without re-creating either callback.
  const drainRef = useRef<() => Promise<boolean>>(() => Promise.resolve(true));

  const setStatusIfMounted = (next: AutosaveStatus) => {
    if (!unmounted.current) setStatus(next);
  };

  const doSave = useCallback(async (): Promise<boolean> => {
    const job = pending.current;
    if (!job || !enabledRef.current) return true;
    pending.current = null;
    if (lastSaved.current.get(job.scope) === job.key) return true;
    setStatusIfMounted('saving');
    try {
      const response = await saveRef.current(job.patch);
      lastSaved.current.set(job.scope, job.key);
      retries.current = 0;
      error.current = null;
      onSavedRef.current?.(response);
      // A newer edit queued while this request was in flight keeps the
      // "Saving..." label; its own save will settle the status.
      if (!pending.current) setStatusIfMounted('saved');
      return true;
    } catch (e) {
      error.current = e;
      // Keep the failed patch unless a newer edit already superseded it.
      // An explicit (Continue) save may carry fields autosave must never send,
      // so it is reported to the caller instead of retried in the background.
      if (!pending.current && !job.explicit) pending.current = job;
      setStatusIfMounted('error');
      if (!unmounted.current && pending.current && retries.current < MAX_AUTO_RETRIES) {
        retries.current += 1;
        retryTimer.current = setTimeout(() => {
          retryTimer.current = null;
          void drainRef.current();
        }, RETRY_BASE_MS * retries.current);
      }
      return false;
    }
  }, []);

  const drain = useCallback((): Promise<boolean> => {
    chain.current = chain.current.then(doSave, doSave);
    return chain.current;
  }, [doSave]);
  drainRef.current = drain;

  const flush = useCallback(async (): Promise<boolean> => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = null;
    if (retryTimer.current) clearTimeout(retryTimer.current);
    retryTimer.current = null;
    return drain();
  }, [drain]);

  const schedule = useCallback(
    (scope: string, patch: BuyerVerificationDraft, options?: { explicit?: boolean }) => {
      if (!enabledRef.current) return;
      const key = JSON.stringify(patch);
      if (lastSaved.current.get(scope) === key) {
        // Edited back to what the server has: nothing to send.
        if (pending.current?.scope === scope) pending.current = null;
        if (!pending.current && debounceTimer.current) {
          clearTimeout(debounceTimer.current);
          debounceTimer.current = null;
        }
        return;
      }
      pending.current = { scope, patch, key, explicit: options?.explicit === true };
      retries.current = 0;
      if (retryTimer.current) clearTimeout(retryTimer.current);
      retryTimer.current = null;
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        debounceTimer.current = null;
        void drain();
      }, debounceMs);
    },
    [drain, debounceMs],
  );

  const markSaved = useCallback((scope: string, patch: BuyerVerificationDraft) => {
    lastSaved.current.set(scope, JSON.stringify(patch));
  }, []);

  const lastError = useCallback(() => error.current, []);

  // Best effort on tab hide / navigation away.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') void flush();
    };
    const onPageHide = () => void flush();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [flush]);

  // Unmount: persist whatever is still pending, then stop all timers.
  useEffect(() => {
    unmounted.current = false;
    return () => {
      void flush();
      unmounted.current = true;
    };
  }, [flush]);

  return { status, schedule, markSaved, flush, lastError };
}
